import { useCallback, useEffect, useRef, useState } from "react";
import type { LandscapeId } from "../data/landscapes";
import { STORY_STEPS, type StoryStepId } from "../data/steps";
import { StoryChangeStep } from "./StoryChangeStep";
import { StoryChapter } from "./StoryChapter";
import { StoryCommunityStep } from "./StoryCommunityStep";
import { StoryHowStep } from "./StoryHowStep";
import { STORY_IMAGES } from "./StoryMedia";
import { StoryProgress } from "./StoryProgress";
import { StoryServicesStep } from "./StoryServicesStep";
import { StoryWhereStep } from "./StoryWhereStep";
import { StoryWhyStep } from "./StoryWhyStep";

/** Where the "pen" sits in the viewport, as a share of its height. */
const DRAW_ANCHOR = 0.42;

/**
 * Tracks how far the journey has been read: exposes the drawn ratio of the path
 * as a CSS variable, and tells which chapter the anchor currently sits in.
 */
function useDrawnPath() {
  const journeyRef = useRef<HTMLDivElement | null>(null);
  const [active, setActive] = useState<StoryStepId>(1);

  useEffect(() => {
    const journey = journeyRef.current;
    if (!journey) return;
    let frame = 0;

    const measure = () => {
      frame = 0;
      const box = journey.getBoundingClientRect();
      if (box.height <= 0) return;

      const anchor = window.innerHeight * DRAW_ANCHOR;
      // The pen can never travel the last (1 - anchor) of a viewport, so shrink
      // the span it has to cover — otherwise the stroke never finishes.
      const span = Math.max(1, box.height - window.innerHeight * (1 - DRAW_ANCHOR) * 0.85);
      const drawn = Math.min(1, Math.max(0, (anchor - box.top) / span));
      journey.style.setProperty("--nbs-journey-drawn", drawn.toFixed(4));

      let current: StoryStepId = 1;
      for (const node of journey.querySelectorAll<HTMLElement>(".nbs-story-chapter")) {
        const step = Number(node.dataset.step);
        if (node.getBoundingClientRect().top <= anchor && step >= 1 && step <= 6) {
          current = step as StoryStepId;
        }
      }
      setActive(current);
    };

    const schedule = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  return { journeyRef, active, setActive };
}

export function StoryEntry() {
  const [selectedId, setSelectedId] = useState<LandscapeId>("madina-oula");
  const [opened, setOpened] = useState<readonly StoryStepId[]>([]);
  const { journeyRef, active, setActive } = useDrawnPath();

  const goTo = useCallback(
    (next: StoryStepId) => {
      setOpened((prev) => (prev.includes(next) ? prev : [...prev, next]));
      setActive(next);
      const chapter = document.getElementById(`etape-${next}`);
      if (chapter && typeof chapter.scrollIntoView === "function") {
        chapter.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [setActive],
  );

  const chapterContent = (id: StoryStepId) => {
    if (id === 1) return <StoryWhyStep />;
    if (id === 2) {
      return (
        <StoryWhereStep
          selectedId={selectedId}
          onSelect={setSelectedId}
          onBack={() => goTo(1)}
          onContinue={() => goTo(3)}
        />
      );
    }
    if (id === 3) return <StoryHowStep onBack={() => goTo(2)} onContinue={() => goTo(4)} />;
    if (id === 4) {
      return (
        <StoryChangeStep
          selectedId={selectedId}
          onSelect={setSelectedId}
          onBack={() => goTo(3)}
          onContinue={() => goTo(5)}
        />
      );
    }
    if (id === 5) {
      return <StoryCommunityStep onBack={() => goTo(4)} onContinue={() => goTo(6)} />;
    }
    return (
      <StoryServicesStep selectedId={selectedId} onSelect={setSelectedId} onBack={() => goTo(5)} />
    );
  };

  return (
    <div className="nbs-story" data-testid="story-entry">
      <header className="nbs-story-hero">
        <div className="nbs-story-hero__backdrop" aria-hidden="true">
          <img className="nbs-story-hero__img" src={STORY_IMAGES.forest} alt="" />
          <div className="nbs-story-hero__veil" />
        </div>
        <div className="nbs-story-hero__inner">
          <h1 className="nbs-story-hero__title">
            Suivi et évaluation des services écosystémiques des paysages forestiers de Kounounkan et
            Madina-Oula de Guinee
          </h1>
          <p className="nbs-story-hero__lead">
            Forêts, mangroves et végétation — un parcours pour comprendre les enjeux, les
            territoires, la méthode, puis les services que rendent ces écosystèmes.
          </p>
        </div>
      </header>

      <StoryProgress step={active} onStepChange={goTo} />

      <div className="nbs-story-journey" ref={journeyRef}>
        <span className="nbs-story-journey__rail" aria-hidden="true">
          <span className="nbs-story-journey__rail-track" />
          <span className="nbs-story-journey__rail-fill" />
        </span>

        {STORY_STEPS.map((item) => (
          <StoryChapter
            key={item.id}
            id={item.id}
            label={item.label}
            hint={item.hint}
            eager={item.id === 1}
            opened={opened.includes(item.id)}
            reached={active >= item.id}
          >
            {chapterContent(item.id)}
          </StoryChapter>
        ))}

        <span className="nbs-story-journey__close" aria-hidden="true" />
      </div>
    </div>
  );
}
